import React from 'react';
import { Link } from 'react-router-dom';
import { EyeOffIcon, MessageSquareIcon, PinIcon } from 'lucide-react';
import { canModerate, useAcademyHub } from '../../../context/AcademyHub';
import { inputClass } from '../../ui/Field';
import { timeAgo } from '../../../utils/academyFormat';
type Filter = 'all' | 'unanswered';

/** Every thread across every channel, so staff can see where learners need an answer. */
export function AdminForum() {
  const {
    hub,
    channels,
    role,
    togglePin,
    hideThread
  } = useAcademyHub();
  const [filter, setFilter] = React.useState<Filter>('unanswered');
  const [channel, setChannel] = React.useState('');
  const rows = hub.threads.filter(t => !t.hidden && (!channel || t.channel === channel)).map(t => {
    const replies = hub.replies.filter(r => r.threadId === t.id && !r.hidden);
    const staffReplied = t.author.staff || replies.some(r => r.author.staff);
    return {
      thread: t,
      replies: replies.length,
      staffReplied
    };
  }).filter(r => filter === 'all' || !r.staffReplied).sort((a, b) => b.thread.createdAt.localeCompare(a.thread.createdAt));
  const unansweredCount = hub.threads.filter(t => !t.hidden && !t.author.staff && !hub.replies.some(r => r.threadId === t.id && !r.hidden && r.author.staff)).length;
  return <div data-id="element-166" data-mp="x1qfl9n85t24">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-167" data-mp="x0xhex2c0bvl">
        <div role="tablist" aria-label="Filter threads" className="flex gap-1 rounded-full border border-line p-1" data-id="element-168" data-mp="1bq3cc8u4zfxo">
          {([['unanswered', `Needs a staff reply (${unansweredCount})`], ['all', 'All threads']] as [Filter, string][]).map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={filter === id} onClick={() => setFilter(id)} className={`rounded-full px-4 py-1.5 text-[13px] transition-colors duration-150 ${filter === id ? 'bg-gold text-charcoal' : 'text-body hover:text-ink'}`} data-id="element-169" data-mp="1fszu5xbur80k">
              {label}
            </button>)}
        </div>
        <label htmlFor="forum-channel" className="sr-only" data-id="element-170" data-mp="1vemimqtv6w7i">
          Channel
        </label>
        <select id="forum-channel" value={channel} onChange={e => setChannel(e.target.value)} className={`${inputClass} w-auto max-w-xs py-2 text-[14px]`} data-id="element-171" data-mp="1zgpxzqjnmky2">
          <option value="" data-id="element-172" data-mp="29dvrt6jnkq1z">All channels</option>
          {channels.map(c => <option key={c.slug} value={c.slug} data-id="element-173" data-mp="2e4u7q50qbm4p">
              {c.name}
            </option>)}
        </select>
      </div>

      {rows.length ? <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-174" data-mp="2j6jj5lrv61tb">
          {rows.map(({
        thread: t,
        replies,
        staffReplied
      }) => {
        const ch = channels.find(c => c.slug === t.channel);
        return <li key={t.id} className="grid gap-3 py-4 sm:grid-cols-[1fr_auto] sm:items-center" data-id="element-175" data-mp="ghjnlaq1cbvf">
                <div className="min-w-0" data-id="element-176" data-mp="biylf5r83i4c">
                  <Link to={`/academy/community/${t.channel}/${t.id}`} className="text-[15px] font-medium text-ink hover:text-royal" data-id="element-177" data-mp="bhxb53i16zno">
                    {t.pinned && <PinIcon className="mr-1.5 inline h-3.5 w-3.5 -translate-y-px text-gold" aria-label="Pinned" data-id="element-178" data-mp="2i0h0mcvh8l8" />}
                    {t.title}
                  </Link>
                  <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-small text-muted" data-id="element-179" data-mp="z7hsxj2g7qvd">
                    <span data-id="element-180" data-mp="v77mk5aquyfz">{t.author.name}</span>
                    <span data-id="element-181" data-mp="qfpktf0wctty">{timeAgo(t.createdAt)}</span>
                    {ch && <span className="truncate" data-id="element-182" data-mp="qdv1241d37rg">{ch.name}</span>}
                    <span className="inline-flex items-center gap-1" data-id="element-183" data-mp="lftlht359qkq">
                      <MessageSquareIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-184" data-mp="1iw0frtzzes6w" />
                      {replies}
                    </span>
                    <span className={staffReplied ? 'text-body' : 'text-royal'} data-id="element-185" data-mp="1e3z5ueqaddk1">{staffReplied ? 'Staff replied' : 'Awaiting staff reply'}</span>
                  </p>
                </div>
                <div className="flex items-center gap-4" data-id="element-186" data-mp="12px6anqn9oa4">
                  <Link to={`/academy/community/${t.channel}/${t.id}`} className="text-small text-royal underline underline-offset-4 hover:text-ink" data-id="element-187" data-mp="opme9hdlm3nd">
                    Reply
                  </Link>
                  <button type="button" onClick={() => togglePin(t.id)} className="inline-flex items-center gap-1 text-small text-muted hover:text-ink" data-id="element-188" data-mp="1hetnpbelcdne">
                    <PinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-189" data-mp="17oqm4mtelrdg" />
                    {t.pinned ? 'Unpin' : 'Pin'}
                  </button>
                  {canModerate(role) && <button type="button" onClick={() => hideThread(t.id)} className="inline-flex items-center gap-1 text-small text-muted hover:text-ink" data-id="element-190" data-mp="20ccnrpj2mlxo">
                      <EyeOffIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-191" data-mp="254xhys5w311q" />
                      Hide
                    </button>}
                </div>
              </li>;
      })}
        </ul> : <p className="mt-6 rounded-md border border-dashed border-line px-5 py-8 text-center text-[14px] text-muted" data-id="element-192" data-mp="2o7y41n7qszcv">
          {filter === 'unanswered' ? 'Every thread has a reply from the Academy team.' : 'No threads in this channel yet.'}
        </p>}
      <p className="mt-3 text-small text-muted" data-id="element-193" data-mp="1vlmuvo62lq4g">Replies you post in a thread carry the Academy team badge.</p>
    </div>;
}